import type { MoveProfile } from '@easymoving/types';
import type { ItemStats } from './itemStats';

export type ReadinessKey = 'items' | 'decisions' | 'packed' | 'boxes' | 'profile';

export interface ReadinessBreakdown {
  score: number; // 0-100
  parts: { key: ReadinessKey; earned: number; weight: number }[];
}

const WEIGHTS: Record<ReadinessKey, number> = {
  items: 10, // added at least one item
  decisions: 30, // share of items with a decided destination
  packed: 25, // share of items packed
  boxes: 10, // created at least one box
  profile: 25, // share of move-profile fields filled
};

/** The profile fields that count toward completion, given the chosen scope. */
function relevantProfileFields(profile: MoveProfile): (keyof MoveProfile)[] {
  const fields: (keyof MoveProfile)[] = ['moveDate', 'moveType'];
  const origin = profile.originScope ?? 'home';
  const destination = profile.destinationScope ?? 'home';
  fields.push(origin === 'room' ? 'sourceRoom' : 'currentHomeSize');
  if (destination === 'home') fields.push('destinationHomeSize');
  return fields;
}

/** Whole days from `nowMs` until `targetMs` (negative if past, 0 if today). */
export function daysUntil(targetMs: number, nowMs: number): number {
  const DAY = 86_400_000;
  return Math.floor(targetMs / DAY) - Math.floor(nowMs / DAY);
}

/** Fraction (0-1) of move-profile fields that have been answered. */
export function profileCompletion(profile: MoveProfile | null): number {
  if (!profile) return 0;
  const fields = relevantProfileFields(profile);
  const filled = fields.filter((f) => profile[f] !== undefined && profile[f] !== null).length;
  return filled / fields.length;
}

/**
 * Move-readiness score (PRD §10.2): a 0-100 sense of progress from inventory +
 * move profile. Pure → unit tested.
 */
export function computeReadiness(
  stats: ItemStats,
  boxCount: number,
  profile: MoveProfile | null,
): ReadinessBreakdown {
  const total = stats.total;
  const decidedShare = total > 0 ? (total - stats.byDestination.undecided) / total : 0;
  const packedShare = total > 0 ? stats.packed / total : 0;

  const parts = [
    { key: 'items' as const, earned: total > 0 ? WEIGHTS.items : 0, weight: WEIGHTS.items },
    { key: 'decisions' as const, earned: WEIGHTS.decisions * decidedShare, weight: WEIGHTS.decisions },
    { key: 'packed' as const, earned: WEIGHTS.packed * packedShare, weight: WEIGHTS.packed },
    { key: 'boxes' as const, earned: boxCount > 0 ? WEIGHTS.boxes : 0, weight: WEIGHTS.boxes },
    { key: 'profile' as const, earned: WEIGHTS.profile * profileCompletion(profile), weight: WEIGHTS.profile },
  ];

  const score = Math.round(parts.reduce((sum, p) => sum + p.earned, 0));
  return { score: Math.max(0, Math.min(100, score)), parts };
}
